import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, fmtTime, rows, type BiometricDevice, type ID, type Roster, type Shift } from '@hrms/shared';
import { api } from '../../../lib/api';
import { todayISO } from '../../../lib/date';
import { fieldErrors } from '../../../lib/errors';
import { useMut } from '../../../lib/query';
import { copyText } from '../components/clipboard';
import { CrudList } from '../components/CrudList';
import { EmployeePicker } from '../components/EmployeePicker';
import { optLabel, useOrgOptions } from '../components/orgOptions';
import {
  Alert, Badge, Button, ConfirmDialog, DataTable, DateField, Grid, IconButton, Modal, PageHeader, Row, Screen, Select, Stack, Tabs, Text, radius, useTheme, useToast,
} from '../../../ui';

type TabKey = 'shifts' | 'rosters' | 'devices';

function RosterTab() {
  const { colors } = useTheme();
  const shifts = useQuery({ queryKey: ['shifts', 'all'], queryFn: () => api.attendance.shifts.list({ per_page: 200 }) });
  const shiftList = rows(shifts.data);
  const [page, setPage] = useState(1);
  const q = useQuery({ queryKey: ['rosters', page], queryFn: () => api.attendance.rosters.list({ page }) });
  const [edit, setEdit] = useState<Partial<Roster> | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [del, setDel] = useState<ID | null>(null);
  const save = useMut(
    () => {
      const b = { employee_id: edit!.employee_id, effective_from: edit!.effective_from, effective_to: edit!.effective_to || null, pattern: edit!.pattern ?? [], cycle_days: edit!.pattern?.length ?? 1 };
      return edit!.id ? api.attendance.rosters.update(edit!.id, b) : api.attendance.rosters.create(b);
    },
    { invalidate: [['rosters']], success: 'Roster saved', onSuccess: () => setEdit(null), onError: (e) => setErrs(fieldErrors(e)) },
  );
  const remove = useMut((id: ID) => api.attendance.rosters.remove(id), { invalidate: [['rosters']], success: 'Roster deleted', onSuccess: () => setDel(null) });
  const shiftCode = (id: ID) => (id === 0 ? 'OFF' : shiftList.find((s) => s.id === id)?.code ?? `#${id}`);
  const pattern = edit?.pattern ?? [];
  const setPattern = (p: ID[]) => setEdit({ ...edit, pattern: p });
  const s0 = shiftList[0]?.id;
  const s1 = shiftList[1]?.id;
  const s2 = shiftList[2]?.id;
  const presets: { label: string; build: () => ID[] }[] = [
    ...(s0 ? [{ label: '5 on / 2 off', build: () => [s0, s0, s0, s0, s0, 0, 0] }] : []),
    ...(s0 && s1 ? [{ label: 'Weekly A→B', build: () => [s0, s0, s0, s0, s0, 0, 0, s1, s1, s1, s1, s1, 0, 0] }] : []),
    ...(s0 && s1 && s2 ? [{ label: '2-2-2 + 2 off', build: () => [s0, s0, s1, s1, s2, s2, 0, 0] }] : []),
  ];
  const shiftOpts = [{ value: 0, label: 'Week off' }, ...shiftList.map((s) => ({ value: s.id, label: `${s.code} · ${s.start_time}–${s.end_time}` }))];
  return (
    <Stack gap={12}>
      <Row>
        <Button size="sm" variant="primary" icon="plus" title="Assign roster" onPress={() => { setEdit({ effective_from: todayISO(), pattern: s0 ? [s0, s0, s0, s0, s0, 0, 0] : [] }); setErrs({}); }} testID="roster-new" />
      </Row>
      <DataTable
        rows={q.data && !Array.isArray(q.data) ? q.data : rows(q.data)}
        loading={q.isLoading}
        onPageChange={setPage}
        onRowPress={(r) => { setEdit(r); setErrs({}); }}
        emptyTitle="No rotating rosters"
        emptyDescription="Employees without a roster use their grade's default shift."
        columns={[
          { key: 'employee', title: 'Employee', width: 180, flex: 2, sortValue: (r) => r.employee?.full_name, render: (r) => (r.employee ? `${r.employee.full_name} (${r.employee.emp_code})` : `#${r.employee_id}`) },
          { key: 'effective_from', title: 'From', width: 110, render: (r) => fmtDate(r.effective_from), sortValue: (r) => r.effective_from },
          { key: 'effective_to', title: 'To', width: 110, render: (r) => fmtDate(r.effective_to), sortValue: (r) => r.effective_to },
          { key: 'pattern', title: 'Pattern', width: 260, flex: 3, sortable: false, render: (r) => <Row gap={3}>{r.pattern.map((p, i) => <Badge key={i} tone={p === 0 ? 'neutral' : 'primary'}>{shiftCode(p)}</Badge>)}</Row> },
          { key: 'cycle_days', title: 'Cycle', width: 70, align: 'right', render: (r) => `${r.cycle_days}d`, sortValue: (r) => r.cycle_days },
          { key: 'act', title: '', width: 56, flex: 0, sortable: false, render: (r) => <IconButton icon="trash" size="sm" label="Delete roster" onPress={() => setDel(r.id)} /> },
        ]}
      />
      <Modal
        open={!!edit}
        onClose={() => setEdit(null)}
        title={edit?.id ? 'Edit roster' : 'Assign rotating roster'}
        width={720}
        footer={
          <>
            <Button title="Cancel" onPress={() => setEdit(null)} />
            <Button variant="primary" title="Save" loading={save.isPending} disabled={!edit?.employee_id || !pattern.length || !edit?.effective_from} onPress={() => save.mutate(undefined)} testID="roster-save" />
          </>
        }
      >
        <Grid min={200} gap={14}>
          <EmployeePicker label="Employee" required error={errs.employee_id} value={edit?.employee_id} onChange={(v) => setEdit({ ...edit, employee_id: v ?? undefined })} />
          <DateField label="Effective from" required error={errs.effective_from} value={edit?.effective_from} onChange={(d) => setEdit({ ...edit, effective_from: d })} />
          <DateField label="Effective to" error={errs.effective_to} value={edit?.effective_to} clearable onChange={(d) => setEdit({ ...edit, effective_to: d || null })} />
        </Grid>
        <Row justify="space-between">
          <Text weight="semibold">Rotation pattern ({pattern.length} days)</Text>
          <Row gap={4}>
            {presets.map((p) => (
              <Button key={p.label} size="sm" variant="ghost" title={p.label} onPress={() => setPattern(p.build())} />
            ))}
          </Row>
        </Row>
        <Text variant="small">Day 1 starts on the effective-from date; the pattern repeats. "Week off" is stored as 0.</Text>
        <Grid min={120} gap={8}>
          {pattern.map((sid, i) => (
            <View key={i} style={{ borderWidth: 1, borderColor: colors.border, borderRadius: radius.md, padding: 6, gap: 4 }}>
              <Row justify="space-between" wrap={false}>
                <Text variant="small">Day {i + 1}</Text>
                <IconButton icon="x" size="sm" label={`Remove day ${i + 1}`} onPress={() => setPattern(pattern.filter((_, j) => j !== i))} />
              </Row>
              <Select<number> value={sid} options={shiftOpts} onChange={(v) => setPattern(pattern.map((p, j) => (j === i ? v : p)))} testID={`roster-day-${i}`} />
            </View>
          ))}
          <Button icon="plus" title="Add day" onPress={() => setPattern([...pattern, pattern[pattern.length - 1] ?? s0 ?? 0])} testID="roster-add-day" />
        </Grid>
        {!!errs.pattern && <Alert tone="danger">{errs.pattern}</Alert>}
      </Modal>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={() => del && remove.mutate(del)} title="Delete roster?" danger confirmLabel="Delete" loading={remove.isPending} />
    </Stack>
  );
}

function DevicesTab() {
  const org = useOrgOptions();
  const toast = useToast();
  const [key, setKey] = useState<string | null>(null);
  return (
    <>
      <CrudList<BiometricDevice>
        embedded
        title="Biometric devices"
        singular="Device"
        description="Devices push punches using their API key."
        resource={api.attendance.devices}
        queryKey={['biometric-devices']}
        onSaved={(r, created) => created && r.api_key && setKey(r.api_key)}
        columns={[
          { key: 'name', title: 'Name', width: 160, flex: 2 },
          { key: 'serial_number', title: 'Serial', width: 140, render: (r) => <Text variant="mono">{r.serial_number}</Text>, sortValue: (r) => r.serial_number },
          { key: 'location_id', title: 'Location', width: 150, render: (r) => optLabel(org.locations, r.location_id) },
          { key: 'last_synced_at', title: 'Last sync', width: 150, sortValue: (r) => r.last_synced_at, render: (r) => (r.last_synced_at ? `${fmtDate(r.last_synced_at)} ${fmtTime(r.last_synced_at)}` : <Badge tone="warning">Never</Badge>) },
        ]}
        fields={[
          { key: 'name', label: 'Name', required: true },
          { key: 'serial_number', label: 'Serial number', required: true },
          { key: 'location_id', label: 'Location', type: 'select', options: org.locations },
        ]}
      />
      <Modal
        open={!!key}
        onClose={() => setKey(null)}
        title="Device API key"
        width={480}
        footer={
          <Button
            variant="primary"
            title="Copy & close"
            onPress={() =>
              void copyText(key ?? '').then((r) => {
                if (r === 'failed') return toast.error('Copy failed — select the key above and copy it manually');
                if (r === 'copied') toast.success('API key copied');
                setKey(null);
              })
            }
          />
        }
      >
        <Alert tone="warning">This key is shown only once. Configure it on the device / sync agent now.</Alert>
        <Text selectable variant="mono" testID="device-api-key">
          {key}
        </Text>
      </Modal>
    </>
  );
}

/** Shifts, rotating rosters and biometric devices. */
export function ShiftsScreen() {
  const [tab, setTab] = useState<TabKey>('shifts');
  return (
    <Screen testID="shifts">
      <PageHeader title="Shifts & rosters" subtitle="Shift timings, rotating rosters and biometric devices." />
      <Tabs<TabKey> value={tab} onChange={setTab} tabs={[{ key: 'shifts', label: 'Shifts' }, { key: 'rosters', label: 'Rotating rosters' }, { key: 'devices', label: 'Biometric devices' }]} />
      {tab === 'shifts' && (
        <CrudList<Shift>
          embedded
          title="Shifts"
          singular="Shift"
          resource={api.attendance.shifts}
          queryKey={['shifts']}
          columns={[
            { key: 'code', title: 'Code', width: 80 },
            { key: 'name', title: 'Name', width: 150, flex: 2 },
            { key: 'timing', title: 'Timing', width: 130, sortValue: (s) => s.start_time, render: (s) => `${s.start_time} – ${s.end_time}${s.crosses_midnight ? ' (+1)' : ''}` },
            { key: 'grace_minutes', title: 'Grace', width: 70, align: 'right', render: (s) => `${s.grace_minutes}m`, sortValue: (s) => s.grace_minutes },
            { key: 'break_minutes', title: 'Break', width: 70, align: 'right', render: (s) => `${s.break_minutes}m`, sortValue: (s) => s.break_minutes },
            { key: 'full_day_minutes', title: 'Full day', width: 80, align: 'right', render: (s) => `${(s.full_day_minutes / 60).toFixed(1)}h`, sortValue: (s) => s.full_day_minutes },
            { key: 'half_day_minutes', title: 'Half day', width: 80, align: 'right', render: (s) => `${(s.half_day_minutes / 60).toFixed(1)}h`, sortValue: (s) => s.half_day_minutes },
            { key: 'ot_threshold_minutes', title: 'OT after', width: 80, align: 'right', render: (s) => `${s.ot_threshold_minutes}m`, sortValue: (s) => s.ot_threshold_minutes },
          ]}
          fields={[
            { key: 'code', label: 'Code', required: true },
            { key: 'name', label: 'Name', required: true },
            { key: 'start_time', label: 'Start time', type: 'time', required: true, default: '09:30' },
            { key: 'end_time', label: 'End time', type: 'time', required: true, default: '18:30' },
            { key: 'grace_minutes', label: 'Grace (min)', type: 'number', default: 15 },
            { key: 'break_minutes', label: 'Break (min)', type: 'number', default: 60 },
            { key: 'full_day_minutes', label: 'Full day (min)', type: 'number', default: 480 },
            { key: 'half_day_minutes', label: 'Half day (min)', type: 'number', default: 240 },
            { key: 'ot_threshold_minutes', label: 'OT after (min beyond full day)', type: 'number', default: 60 },
            { key: 'crosses_midnight', label: 'Crosses midnight (night shift)', type: 'checkbox' },
          ]}
        />
      )}
      {tab === 'rosters' && <RosterTab />}
      {tab === 'devices' && <DevicesTab />}
    </Screen>
  );
}
